<template>
  <a-page-header class="invoice-header">
    <template #title>
      {{ parentNames }}
    </template>

    <!-- Student Select Section with Alert -->
    <div class="student-select-section">
      <div class="student-select-container">
        <label>{{ $t("menu.listeEleves") }} : </label>
        <div v-if="loadingStudents" class="student-select-skeleton">
          <a-skeleton active :paragraph="{ rows: 1 }" />
        </div>
        <a-select
          v-else
          :allowClear="false"
          @change="handleStudentChange"
          mode="multiple"
          :placeholder="$t('menu.listeEleves')"
          option-filter-prop="children"
          class="w-50"
          :default-value="selectedStudents"
        >
          <a-select-option
            v-for="student in activeStudents"
            :key="student._id"
            :value="student._id"
          >
            {{ getStudentFullName(student) }}
          </a-select-option>
        </a-select>
      </div>

      <!-- Caisse Status Alert -->
      <div class="caisse-alert-container" v-if="caisseStats">
        <a-alert
          v-if="caisseStats.availability === 'CLOSED'"
          :message="$t('caisse.caisseFermeeMessage')"
          :description="$t('caisse.caisseOuvertureAutomatique')"
          type="warning"
          show-icon
          banner
          class="caisse-alert"
        />
      </div>
    </div>

    <!-- AddressedTo Section -->
    <div class="addressed-to-section">
      <div class="addressed-to-display">
        <h4>{{ addressedToTitle }}</h4>
        <div class="addressed-to-text">

          {{ addressedToDisplayText }}
          <template v-if="!isAddressedToValid">
            <a-icon type="warning" theme="twoTone" two-tone-color="red"  class="ml-2" />
          </template>
          <a-button type="link" @click="openEditModal">
            <a-icon type="edit" />
          </a-button>
        </div>
      </div>
    </div>

    <!-- Addressed To Modal -->
    <AddressedToModal
      :visible="showEditModal"
      :initial-data="addressedTo"
      :selected-type-f-r="selectedTypeFR"
      :father-name="fatherName"
      :mother-name="motherName"
      :selected-student-ids="selectedStudents"
      @ok="handleModalOk"
      @cancel="closeEditModal"
      @defaults-set="handleDefaultsSet"
    />

    <!-- Caisse Details Modal -->
    <a-modal
      :visible="showCaisseModal"
      @cancel="closeCaisseModal"
      :footer="null"
      width="800px"
    >
      <CaisseDetails
        :stats="caisseStats"
        :loading="caisseStatsLoading"
        :showName="true"
        :showAuthorizedUsers="true"
        :title="$t('menu.myCaisse')"
      />
    </a-modal>

    <template #extra>
      <a-space :size="16">
        <a-select
          :value="selectedPaymentMethod"
          @change="handlePaymentMethodChange"
          mode="multiple"
          show-search
          style="width: 100%"
          option-filter-prop="children"
          :placeholder="$t('recette.typePaiement')"
        >
          <a-select-option
            v-for="method in paymentMethods"
            :key="method.value"
            :value="method.value"
          >
            {{ method.label }}
          </a-select-option>
        </a-select>

        <a-radio-group
          default-value="R"
          button-style="solid"
          v-model="selectedTypeFR"
          @change="handlePaymentTypeFRChange"
        >
          <a-radio-button
            v-for="type in documentTypes"
            :key="type.value"
            :value="type.value"
          >
            <a-icon :type="type.icon" /> {{ type.label }}
          </a-radio-button>
        </a-radio-group>

        <!-- A user can own several caisses: he picks the one that receives the payment. -->
        <a-select
          v-if="myCaisses.length > 1"
          :value="selectedCaisseId"
          style="min-width: 200px"
          :placeholder="$t('caisse.selectionnerCaisse')"
          @change="handleCaisseChange"
        >
          <a-select-option v-for="c in myCaisses" :key="c._id" :value="c._id">
            {{ c.name }}
          </a-select-option>
        </a-select>

        <a-button
          type="default"
          @click="openCaisseModal"
          :loading="caisseStatsLoading"
          :disabled="caisseStatsLoading || !caisseStats"
        >
          <a-icon type="bank" />
          {{ $t("menu.myCaisse") || "Caisse" }}
        </a-button>

        <a-button
          type="primary"
          :loading="loadingValider"
          :disabled="
            loadingValider ||
            !isAddressedToValid ||
            disabledValider ||
            disableInvalidAvance
          "
          @click="handlePaymentValidation"
        >
          {{ $t("recette.valider") }}
        </a-button>
      </a-space>
    </template>
  </a-page-header>
</template>

<script>
import { Space } from "ant-design-vue";
import { mapState } from "vuex";
import apiClient from "@/services/axios";
import CaisseDetails from "@/views/compV3/caisse/components/CaisseDetails.vue";
import AddressedToModal from "./AddressedToModal.vue";

export default {
  name: "InvoiceHeader",
  components: {
    "a-space": Space,
    CaisseDetails,
    AddressedToModal,
  },

  props: {
    selectedStudents: {
      required: true,
      type: Array,
    },
    disabledValider: {
      required: true,
      type: Boolean,
    },
    parent: {
      required: false,
      type: Object,
      default: () => ({}),
    },
    // Main student already set: only offer the parent's students, never the school list.
    restrictToParent: {
      type: Boolean,
      default: false,
    },
    loadingValider: {
      required: true,
      type: Boolean,
    },
    disableInvalidAvance: {
      type: Boolean,
      default: false,
    },
    selectedPaymentMethod: {
      type: Array,
      default: () => ["ESPESE"],
    },
  },

  data() {
    return {
      selectedTypeFR: "R",
      addressedTo: this.getDefaultAddressedTo(),
      showEditModal: false,

      // Caisse modal data
      showCaisseModal: false,
      caisseStats: null,
      caisseStatsLoading: false,
      myCaisses: [],
      selectedCaisseId: null,

      // All students data
      allStudents: [],
      loadingStudents: false,
    };
  },

  computed: {
    ...mapState(["settings"]),
    paymentMethods() {
      return [
        { value: "ESPESE", label: this.$t("recette.espece") },
        { value: "CHEQUE", label: this.$t("recette.cheque") },
        { value: "TRAITE", label: this.$t("recette.traite") },
        { value: "VIREMENT", label: this.$t("recette.virement") },
        { value: "TPE", label: this.$t("recette.TPE") },
      ];
    },
    documentTypes() {
      return [
        { value: "R", label: this.$t("recette.recus"), icon: "file-done" },
        { value: "F", label: this.$t("recette.factureTag"), icon: "file-text" },
      ];
    },
    parentNames() {
      // If no parent is provided, show a generic title
      if (!this.parent || Object.keys(this.parent).length === 0) {
        return this.$t("all.selectionnerEleves");
      }

      const names = [];
      if (this.parent?.father?.firstName) {
        names.push(
          `${this.parent.father.firstName} ${this.parent.father.lastName}`
        );
      }
      if (this.parent?.mother?.firstName) {
        names.push(
          `${this.parent.mother.firstName} ${this.parent.mother.lastName}`
        );
      }
      return names.join(` ${this.$t("all.and")} `);
    },

    fatherName() {
      if (!this.parent || Object.keys(this.parent).length === 0) {
        return "";
      }
      return this.parent?.father?.firstName
        ? `${this.parent.father.firstName} ${this.parent.father.lastName}`
        : "";
    },

    motherName() {
      if (!this.parent || Object.keys(this.parent).length === 0) {
        return "";
      }
      return this.parent?.mother?.firstName
        ? `${this.parent.mother.firstName} ${this.parent.mother.lastName}`
        : "";
    },

    activeStudents() {
      // If parent is provided, use parent's students
      if (
        this.parent &&
        Object.keys(this.parent).length > 0 &&
        this.parent.students
      ) {
        return this.parent.students.filter((s) => s.status === "active");
      }
      if (this.restrictToParent) return [];
      // Otherwise, use all students
      return this.allStudents;
    },

    addressedToTitle() {
      const documentType =
        this.selectedTypeFR === "R"
          ? this.$t("recette.recus")
          : this.$t("recette.factureTag");
      return `${documentType}${this.$t("invoice.addressedTo")}`;
    },

    addressedToDisplayText() {
      if (this.addressedTo.type === "PARTICULAR") {
        const representativeText = this.getRepresentativeText(
          this.addressedTo.representative
        );
        return `${this.addressedTo.name} (${representativeText})`;
      } else if (this.addressedTo.type === "ENTREPRISE") {
        return `${this.addressedTo.name} (MF: ${this.addressedTo.MF})`;
      }
      return this.$t("invoice.selectAddressedTo");
    },

    isAddressedToValid() {
      return this.isFormValid(this.addressedTo);
    },
  },

  methods: {
    // Event handlers
    handlePaymentMethodChange(selectedPaymentMethod, e) {
      this.$emit("select-payment-method", selectedPaymentMethod, e);
    },

    handlePaymentTypeFRChange(selectedTypeFR, e) {
      this.$emit("select-payment-type-fr", selectedTypeFR, e);
    },

    handleStudentChange(selectedStudents, e) {
      this.$emit("select-student", selectedStudents, e);
    },

    handlePaymentValidation() {
      this.validateAddressedTo();
      if (this.isAddressedToValid) {
        const filteredAddressedTo = this.getFilteredAddressedTo();
        this.$emit("valid-payment", this.selectedTypeFR, filteredAddressedTo);
      }
    },

    // Modal handlers
    openEditModal() {
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
    },

    handleModalOk(formData) {
      this.addressedTo = { ...formData };
      this.closeEditModal();
    },

    handleDefaultsSet(defaults) {
      this.addressedTo = { ...this.addressedTo, ...defaults };
    },

    // Caisse modal handlers
    async openCaisseModal() {
      this.caisseStatsLoading = true;
      try {
        await this.loadCaisseStats();
        this.showCaisseModal = true;
      } catch (error) {
        console.error("Error loading caisse stats:", error);
        this.$message.error(
          this.$t("caisse.erreurFermeture")
        );
      } finally {
        this.caisseStatsLoading = false;
      }
    },

    closeCaisseModal() {
      this.showCaisseModal = false;
    },

    handleCaisseChange(caisseId) {
      this.selectedCaisseId = caisseId;
      this.caisseStats = this.myCaisses.find((c) => c._id === caisseId) || null;
      this.$emit("caisse-change", caisseId);
    },

    // Load the caisses the user owns; the payment is credited to the selected one.
    async loadCaisseStats() {
      try {
        const res = await apiClient.get("/compV3/caisse/my-caisses");
        this.myCaisses = res.data || [];
        if (!this.myCaisses.some((c) => c._id === this.selectedCaisseId)) {
          this.handleCaisseChange(
            this.myCaisses.length ? this.myCaisses[0]._id : null
          );
        }
      } catch (error) {
        console.error("Error loading caisse stats:", error);
        // Don't show error message for background loading
      }
    },

    // Validation methods
    validateAddressedTo() {
      // Validation is now handled by the modal component
    },

    validateForm(formData) {
      const errors = {};

      if (!formData.type) {
        errors.type = this.$t("invoice.typeRequired");
      }

      if (!formData.name || formData.name.trim() === "") {
        errors.name = this.$t("invoice.nameRequired");
      }

      if (formData.type === "PARTICULAR") {
        if (!formData.representative) {
          errors.representative = this.$t("invoice.representativeRequired");
        }
      } else if (formData.type === "ENTREPRISE") {
        if (!formData.MF || formData.MF.trim() === "") {
          errors.MF = this.$t("invoice.MFRequired");
        }
      }

      return errors;
    },

    isFormValid(formData) {
      return (
        !Object.values(this.validateForm(formData)).some((error) => error) &&
        formData.name &&
        formData.type
      );
    },

    getDefaultAddressedTo() {
      return {
        name: "",
        type: "PARTICULAR",
        representative: "FATHER",
        MF: "",
      };
    },

    getFilteredAddressedTo() {
      const filtered = {
        name: this.addressedTo.name,
        type: this.addressedTo.type,
      };

      if (this.addressedTo.type === "PARTICULAR") {
        filtered.representative = this.addressedTo.representative;
      } else if (this.addressedTo.type === "ENTREPRISE") {
        filtered.MF = this.addressedTo.MF;
      }

      return filtered;
    },

    // Utility methods
    getStudentFullName(student) {
      return `${student.firstName}${student.middleName ? " " + student.middleName : ""} ${student.lastName}`;
    },

    getRepresentativeText(representative) {
      const representativeMap = {
        FATHER: this.$t("invoice.father"),
        MOTHER: this.$t("invoice.mother"),
        OTHER: this.$t("invoice.other"),
      };
      return representativeMap[representative] || representative;
    },

    // Fetch all students when no parent is provided
    async fetchAllStudents() {
      // Check if parent is provided and has actual data
      if (
        this.parent &&
        Object.keys(this.parent).length > 0 &&
        this.parent._id
      ) {
        // If parent is provided with actual data, don't fetch all students
        console.log("Parent provided, skipping fetchAllStudents");
        return;
      }

      this.loadingStudents = true;
      try {
        // Check if settings and activeSchoolarYear are available
        const activeSchoolarYear = this.settings.activeSchoolarYear;

        const aggregation = [
          {
            $match: {
              status: "active",
              [`schoolarYearsHistory.${activeSchoolarYear}`]: {
                $exists: true,
              },
            },
          },
          {
            $project: {
              _id: 1,
              firstName: 1,
              lastName: 1,
              middleName: 1,
            },
          },
        ];

        const response = await apiClient.post("/students/filter", {
          query: {},
          aggregation: aggregation,
        });
        this.allStudents = response.data;
      } catch (error) {
        console.error("Error fetching all students:", error);
        this.$message.error(this.$t("all.error") || "Error loading students");
      } finally {
        this.loadingStudents = false;
      }
    },
  },

  mounted() {
    // Set default name to father's name when component is mounted
    if (
      this.addressedTo.type === "PARTICULAR" &&
      this.addressedTo.representative === "FATHER" &&
      !this.addressedTo.name
    ) {
      this.addressedTo.name = this.fatherName;
    }

    // Initialize modal data with current values
    this.modalAddressedTo = { ...this.addressedTo };
    if (
      this.parent &&
      Object.keys(this.parent).length === 0 &&
      !this.restrictToParent
    ) {
      this.fetchAllStudents();
    }
    // Load caisse stats to show alert if caisse is closed
    this.loadCaisseStats();
  },
};
</script>

<style scoped>
.invoice-header {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 24px;
}

.invoice-header .ant-page-header-heading-title {
  font-weight: 600;
  font-size: 20px;
}

.invoice-header .ant-page-header-heading-sub-title {
  font-size: 14px;
}

.ant-space-item .ant-select {
  min-width: 200px;
}

.student-select-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.student-select-container {
  flex: 0 0 50%;
}

.student-select-skeleton {
  width: 100%;
  padding: 10px 0;
}

.student-select-skeleton .ant-skeleton {
  width: 100%;
}

.student-select-skeleton .ant-skeleton-paragraph {
  margin-bottom: 0;
}

.caisse-alert-container {
  flex: 1;
  max-width: none;
}

.caisse-alert {
  margin-left: 16px;
  font-size: 12px;
}

.caisse-alert .ant-alert-message {
  font-size: 12px;
  font-weight: 500;
}

.caisse-alert .ant-alert-description {
  font-size: 11px;
  margin-top: 2px;
}

.addressed-to-section {
  margin: 16px 0;
  padding: 0;
}

.addressed-to-section h4 {
  margin-bottom: 0px;
  color: #262626;
  font-weight: 500;
  font-size: 14px;
}

.addressed-to-display {
  display: flex;
  align-items: center;
  gap: 12px;
}

.addressed-to-text {
  flex: 1;
  padding: 6px 0;
  font-size: 14px;
  color: #595959;
  border-bottom: 1px solid #f0f0f0;
  min-height: 24px;
  display: flex;
  align-items: center;
}

.edit-btn {
  padding: 2px 6px;
  height: auto;
  border: none;
  color: #1890ff;
  font-size: 12px;
  background: transparent;
}

.edit-btn:hover {
  color: #40a9ff;
  background: transparent;
}

.w-50 {
  width: 50%;
}

.w-100 {
  width: 100%;
}
</style>
